import React from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import {
  CloudArrowUpIcon,
  CubeIcon,
  CheckCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

export const Dashboard: React.FC = () => {
  const { user } = useAuthStore();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name}!
        </h1>
        <p className="mt-2 text-lg text-gray-600">
          Ready to analyze your 3D models for printability?
        </p>
      </div>

      {/* Main Upload Section */}
      <div className="card text-center py-12">
        <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
          <CloudArrowUpIcon className="w-10 h-10 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Upload Your 3D Model
        </h2>
        <p className="text-gray-600 mb-8 max-w-md mx-auto">
          Get started by uploading a 3D model file. Our AI will analyze it for printability issues and provide optimization recommendations.
        </p>
        <Link to="/upload">
          <Button size="lg" className="px-8">
            <CloudArrowUpIcon className="mr-2 h-5 w-5" />
            Start Analysis
          </Button>
        </Link>
      </div>

      {/* Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center p-6">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Instant Analysis
          </h3>
          <p className="text-gray-600 text-sm">
            Upload STL or OBJ files and get comprehensive printability analysis in seconds.
          </p>
        </div>

        <div className="card text-center p-6">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <CubeIcon className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            3D Visualization
          </h3>
          <p className="text-gray-600 text-sm">
            Interactive 3D viewer to examine your model and understand analysis results.
          </p>
        </div>

        <div className="card text-center p-6">
          <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
            <SparklesIcon className="w-6 h-6 text-yellow-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Smart Recommendations
          </h3>
          <p className="text-gray-600 text-sm">
            Get actionable suggestions to optimize your model for successful 3D printing.
          </p>
        </div>
      </div>

      {/* Account Info */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Account Status
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              You're currently on the <span className="font-medium capitalize">{user?.subscriptionTier || 'free'}</span> plan
            </p>
          </div>
          <div className="flex space-x-3">
            <Link to="/pricing">
              <Button variant="outline">
                {user?.subscriptionTier === 'free' ? 'Upgrade Plan' : 'Manage Plan'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
